import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  MapPin,
  CheckSquare,
  Package,
  FileText,
  AlertTriangle,
  TrendingUp,
  ChevronRight,
  Clock,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagementDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/management/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Fout bij laden dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-tw-blue border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-500">Laden...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Geen data beschikbaar</p>
      </div>
    );
  }

  const { kpis, takenPerLocatie, datum } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-tw-blue flex items-center gap-2">
            <LayoutDashboard className="w-7 h-7" />
            Management Dashboard
          </h1>
          <p className="text-gray-500">Overzicht van alle locaties - {new Date(datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Locations */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Totaal locaties</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{kpis.totaalLocaties}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Today */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Taken vandaag</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">
                  {kpis.takenVandaag.klaar}/{kpis.takenVandaag.totaal}
                </p>
                <div className="mt-2">
                  <Progress value={kpis.takenVandaag.progress} className="h-2" />
                  <p className="text-sm text-slate-500 mt-1">{kpis.takenVandaag.progress}% compleet</p>
                </div>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckSquare className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inventory Alerts */}
        <Link to="/management/voorraad?status=kritiek">
          <Card className={`hover:shadow-md transition-shadow cursor-pointer ${kpis.voorraad.kritiek > 0 ? 'border-red-200' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">Voorraad alerts</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{kpis.voorraad.kritiek}</p>
                  <p className="text-sm text-amber-600 mt-1">
                    +{kpis.voorraad.waarschuwing} waarschuwingen
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${kpis.voorraad.kritiek > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <Package className={`w-6 h-6 ${kpis.voorraad.kritiek > 0 ? 'text-red-600' : 'text-slate-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Contract Alerts */}
        <Link to="/management/contracten?status=binnenkort">
          <Card className={`hover:shadow-md transition-shadow cursor-pointer ${(kpis.contracten.verlopen + kpis.contracten.verlooptBinnenkort) > 0 ? 'border-amber-200' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">Contract alerts</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">
                    {kpis.contracten.verlopen + kpis.contracten.verlooptBinnenkort}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {kpis.contracten.verlopen > 0 && <span className="text-red-600">{kpis.contracten.verlopen} verlopen</span>}
                    {kpis.contracten.verlopen > 0 && kpis.contracten.verlooptBinnenkort > 0 && ', '}
                    {kpis.contracten.verlooptBinnenkort > 0 && <span>{kpis.contracten.verlooptBinnenkort} binnenkort</span>}
                    {kpis.contracten.verlopen === 0 && kpis.contracten.verlooptBinnenkort === 0 && 'Geen alerts'}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${(kpis.contracten.verlopen + kpis.contracten.verlooptBinnenkort) > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                  <FileText className={`w-6 h-6 ${(kpis.contracten.verlopen + kpis.contracten.verlooptBinnenkort) > 0 ? 'text-amber-600' : 'text-slate-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Tasks per Location */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Taakvoortgang per locatie (vandaag)
          </CardTitle>
          <Link to="/management/taken" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            Volledig overzicht <ChevronRight className="w-4 h-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {takenPerLocatie.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Geen taken voor vandaag</p>
            </div>
          ) : (
            <div className="space-y-4">
              {takenPerLocatie.map((loc) => (
                <div key={loc.locatieId} className="flex items-center gap-4">
                  <div className="w-32 truncate">
                    <span className="font-medium text-slate-800">{loc.locatieNaam}</span>
                  </div>
                  <div className="flex-1">
                    <Progress value={loc.progress} className="h-3" />
                  </div>
                  <div className="w-24 text-right">
                    <span className={`font-medium ${loc.progress === 100 ? 'text-green-600' : loc.progress < 50 ? 'text-red-600' : 'text-amber-600'}`}>
                      {loc.klaar}/{loc.totaal}
                    </span>
                    <span className="text-slate-500 ml-1">({loc.progress}%)</span>
                  </div>
                  {loc.progress === 100 && (
                    <Badge variant="secondary" className="bg-green-100 text-green-800">Klaar</Badge>
                  )}
                  {loc.progress < 50 && loc.totaal > 0 && (
                    <Badge variant="secondary" className="bg-red-100 text-red-800">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Achter
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/management/voorraad">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Voorraad overzicht</h3>
                <p className="text-sm text-slate-500">Bekijk voorraad alle locaties</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/management/taken">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckSquare className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Taken overzicht</h3>
                <p className="text-sm text-slate-500">Taakvoortgang per locatie</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/management/contracten">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-amber-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Contracten overzicht</h3>
                <p className="text-sm text-slate-500">Alle leverancierscontracten</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default ManagementDashboard;
